import type { ReactNode } from "react";
import { cx } from "./ui";

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Accessible heading when `header` is empty (an actions column). */
  headerLabel?: string;
  align?: "left" | "right";
  width?: string;
  /** Set on the column the rows are sorted by; rendered as aria-sort. */
  sort?: "ascending" | "descending";
  cell: (row: T) => ReactNode;
  /** Which line of a stacked row this cell sits on below `sm` (default 1). Lines start on a forced break. */
  stackedLine?: 1 | 2 | 3;
  /** Extra cell classes below `sm` when `stacked` (growth, alignment, muted text). */
  stackedClass?: string;
  /** Shown before the cell only when stacked, since the header row is hidden then. */
  stackedLabel?: string;
}

export interface Selectable<T> { selected: Set<string>; onChange: (next: Set<string>) => void; label: (row: T) => string }

const LINE: Record<1 | 2 | 3, string> = { 1: "order-1", 2: "order-2", 3: "order-3" };

/**
 * One table for owners (a few roomy rows) and admins (the fleet, compact, selectable, sticky header), drawn as
 * the Figma table: a tinted 12px header row, 14px medium cells, a divider between rows. It sits inside a
 * `Panel padding="none"`, whose corners the header's end cells follow.
 * `stacked` turns each row into a wrapped flex block below `sm`: the same cells, flowed onto the lines
 * their columns ask for, not a second component. Padding is applied only from `sm` up in that mode
 * because an unprefixed `p-0` would lose to `px-3` in Tailwind's output order.
 */
export function DataTable<T>({ columns, rows, rowKey, density = "default", stickyHeader, stacked, selectable, emptyState, caption }: {
  columns: Column<T>[]; rows: T[]; rowKey: (row: T) => string; density?: "default" | "compact"; stickyHeader?: boolean; stacked?: boolean;
  selectable?: Selectable<T>; emptyState?: ReactNode; caption?: string;
}) {
  if (rows.length === 0 && emptyState) return <>{emptyState}</>;
  const headPad = density === "compact" ? "px-4 py-2.5" : "px-5 py-3";
  const pad = density === "compact" ? "px-4 py-2" : "px-5 py-2.5";
  const cellPad = stacked ? (density === "compact" ? "sm:px-4 sm:py-2" : "sm:px-5 sm:py-2.5") : pad;
  const ids = rows.map(rowKey);
  const allSelected = selectable !== undefined && ids.length > 0 && ids.every((id) => selectable.selected.has(id));
  const toggleAll = () => selectable?.onChange(allSelected ? new Set() : new Set(ids));
  const toggleOne = (id: string, on: boolean) => {
    if (!selectable) return;
    const next = new Set(selectable.selected);
    on ? next.add(id) : next.delete(id);
    selectable.onChange(next);
  };
  const lineOf = (c: Column<T>) => c.stackedLine ?? 1;
  const startsLine = (c: Column<T>) => lineOf(c) > 1 && columns.find((x) => lineOf(x) === lineOf(c)) === c;
  const cellClass = (c: Column<T>) => cx(cellPad, "align-middle text-sm font-medium text-hs-text", c.align === "right" && "text-right tabular-nums",
    stacked && cx("block sm:table-cell", LINE[lineOf(c)], c.stackedClass));
  return (
    // A sticky header must not sit inside a scroll container, or it sticks to a box that never scrolls; from `lg` the page is the scroller.
    <div className={cx("w-full overflow-x-auto", stickyHeader && "lg:overflow-visible")}>
      <table data-stacked={stacked ? "true" : undefined} className={cx("w-full border-collapse", stacked && "block sm:table")}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className={cx("bg-hs-surface-alt text-left text-xs font-semibold text-hs-text-muted [&>tr>th:first-child]:rounded-tl-[19px] [&>tr>th:last-child]:rounded-tr-[19px]", stickyHeader && "sticky top-0 z-10", stacked && "hidden sm:table-header-group")}>
          <tr>
            {selectable && (
              <th scope="col" className={cx(headPad, "w-10")}>
                <input type="checkbox" aria-label={`Select all ${rows.length}`} checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-hs-primary" />
              </th>
            )}
            {columns.map((c) => (
              <th key={c.key} scope="col" style={c.width ? { width: c.width } : undefined} aria-label={c.headerLabel} aria-sort={c.sort} className={cx(headPad, "whitespace-nowrap font-semibold", c.align === "right" && "text-right")}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={cx(stacked && "block sm:table-row-group")}>
          {rows.map((r) => {
            const id = rowKey(r);
            return (
              <tr key={id} className={cx("border-t border-hs-border hover:bg-hs-surface-alt/60", stacked && "flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 sm:table-row sm:p-0")}>
                {selectable && (
                  <td className={cx(cellPad, "align-middle", stacked && cx("block sm:table-cell", LINE[1]))}>
                    <input type="checkbox" aria-label={selectable.label(r)} checked={selectable.selected.has(id)} onChange={(e) => toggleOne(id, e.target.checked)} className="h-4 w-4 accent-hs-primary" />
                  </td>
                )}
                {columns.map((c) => (
                  <Cell key={c.key} className={cellClass(c)} breakBefore={stacked && startsLine(c) ? LINE[lineOf(c)] : null}>
                    {stacked && c.stackedLabel && <span className="mr-1 text-xs text-hs-text-muted sm:hidden">{c.stackedLabel}</span>}
                    {c.cell(r)}
                  </Cell>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** A cell, preceded by a zero-height full-width break when it starts a new stacked line. */
function Cell({ className, breakBefore, children }: { className: string; breakBefore: string | null; children: ReactNode }) {
  return (
    <>
      {breakBefore && <td aria-hidden="true" data-break="" className={cx("h-0 basis-full sm:hidden", breakBefore)} />}
      <td className={className}>{children}</td>
    </>
  );
}
